import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar/AppToolbar';
import { ColumnFinder } from '../../../components/ColumnFinder/ColumnFinder';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../../components/confirm';
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { FormTitle } from '../../../components/FormTitle';
import { ProjectNotesButton } from '../../../components/ProjectNotes';
import { formatCombo } from '../../../components/hotkeys';
import { PRE_LOGIN_FORM, type ChildTab } from './PreLoginSettingsDetailPage';
import PreLoginTestAssignment from './PreLoginTestAssignment';
import { CopySamplesDialog, TargetSamplesDialog, type PickRow } from './PreLoginCopyDialogs';
import { useConfirm } from '../../../components/confirm';
import '../../sampling/sampleRegistration/sampleRegistration.css';
import './preLoginSettings.css';
import type {
  PreLoginSample, PreLoginSampleInput, PreLoginTest, PreLoginBottle, PreLoginParameter, PreLoginLookup,
} from './types';

/** Tab captions, also used in the breadcrumb. */
const TAB_LABEL: Record<ChildTab, string> = {
  samples: 'Samples', tests: 'Tests', containers: 'Containers', parameters: 'Parameters',
};

interface Props {
  cocOid: string;
  /** The record's display id, for the header + breadcrumb. */
  cocId: string | null;
  /** Which collection is showing; this page owns its own tab strip. */
  tab: ChildTab;
  setTab: (t: ChildTab) => void;
  /** Per-COC totals from the header read — the legacy buttons show these.
   *  Tests is DISTINCT TestMethod, not the per-sample row count. */
  testCount: number;
  containerCount: number;
  /** Return to the record's form. */
  onBack: () => void;
}

/**
 * The SAMPLES tab of a Pre-Login Setting — the legacy "Sample" action button's
 * view (DashboardView `COCSettingsSampleRegistration` hosting ListView
 * `COCSettingsSamples_ListView_Copy_SampleRegistration`), plus the per-sample
 * TESTS and BOTTLE ALLOCATION grids the legacy screen shows beneath it
 * (`COCSettingsTest_ListView_Copy_SampleRegistration` and
 * `COCSettingsBottleAllocation_ListView_Copy_Sampleregistration`).
 *
 * <p>Each grid's columns are the legacy ListView's VISIBLE columns, in its
 * order — the legacy nodes hide most of `COCSettingsSamples`' ~190 columns with
 * `Index="-1"`.</p>
 *
 * <p>Samples are edited as a SET: New / Delete buffer locally and Save PUTs the
 * whole set in one transaction (the "Assign Tests To Item" pattern). That is
 * what makes a save atomic — a sample can never be removed while its tests stay
 * live. Tests and bottles are read-only here; they are generated from the test
 * library by the sample-registration flow, exactly as the legacy grids are
 * (`AllowNew="False"` on the bottle node).</p>
 */
export default function PreLoginSamplesPage({
  cocOid, cocId, tab, setTab, testCount, containerCount, onBack,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();

  const [samples, setSamples] = useState<PreLoginSample[]>([]);
  const [tests, setTests]     = useState<PreLoginTest[]>([]);
  const [bottles, setBottles] = useState<PreLoginBottle[]>([]);
  const [params,  setParams]  = useState<PreLoginParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** The sample whose tests/bottles are shown below. */
  const [activeSample, setActiveSample] = useState<string | null>(null);
  /** Locally-buffered edits, keyed by sample Oid. */
  const [edits, setEdits] = useState<Record<string, Partial<PreLoginSample>>>({});
  /** True once the SET has changed — an edit, an add, or a removal. Tracked
   *  separately from the edits map because removing a row leaves no edit entry
   *  behind but still has to enable Save. */
  const [dirty, setDirty] = useState(false);
  /** The samples grid wrapper - Find Column scrolls it. */
  const gridWrapRef = useRef<HTMLDivElement>(null);
  /** Dropdown lists for the grid's combo cells (type / matrix / status). */
  const [lookups, setLookups] = useState<PreLoginLookup[]>([]);
  /** Copy Samples / Copy Test / Copy Bottles popups (server-side operations on
   *  SAVED samples — legacy commits the grid before each). */
  const [copy, setCopy] = useState<'samples' | 'test' | 'bottles' | null>(null);
  const [targets, setTargets] = useState<PickRow[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/pre-login-settings/${cocOid}/samples`);
      const rows: PreLoginSample[] = res.data?.data ?? [];
      setSamples(rows);
      setEdits({});
      setDirty(false);
      setActiveSample(a => (a && rows.some(r => r.oid === a) ? a : rows[0]?.oid ?? null));
    } catch {
      notify.error('Could not load the samples.');
    } finally {
      setLoading(false);
    }
  }, [cocOid, notify]);

  useEffect(() => {
    let alive = true;
    api.get('/api/v1/pre-login-settings/sample-lookups')
      .then(r => { if (alive) setLookups(r.data?.data ?? []); })
      .catch(() => { if (alive) notify.error('Could not load the sample dropdown lists.'); });
    return () => { alive = false; };
  }, [notify]);

  // First open of Samples: the server creates the "No Of Samples" samples (with the
  // header's chosen tests and their bottles) exactly once per COC and answers 0 on
  // every later call - so this is safe to fire on every mount. Failure must never
  // hide the grid, so it is swallowed into a warning and the normal load follows.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.post(`/api/v1/pre-login-settings/${cocOid}/samples/init`);
        const created = res.data?.data?.created ?? 0;
        if (alive && created > 0) {
          notify.success(`${created} sample${created === 1 ? '' : 's'} created from No Of Samples.`);
        }
      } catch {
        if (alive) notify.warning('Could not create the samples from No Of Samples.');
      }
      if (alive) await loadSamples();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cocOid]);

  /** Loads the two child grids for the active sample. Shared by the effect
   *  below and each child grid's own section-toolbar Refresh. */
  const reloadChildren = useCallback(async () => {
    if (!activeSample) { setTests([]); setBottles([]); setParams([]); return; }
    try {
      const [t, b, p] = await Promise.all([
        api.get(`/api/v1/pre-login-settings/samples/${activeSample}/tests`),
        api.get(`/api/v1/pre-login-settings/samples/${activeSample}/bottles`),
        api.get(`/api/v1/pre-login-settings/samples/${activeSample}/parameters`),
      ]);
      setTests(t.data?.data ?? []);
      setBottles(b.data?.data ?? []);
      setParams(p.data?.data ?? []);
    } catch {
      notify.error('Could not load the tests, containers and parameters for this sample.');
    }
  }, [activeSample, notify]);

  // Tests + bottles follow the active sample.
  useEffect(() => { void reloadChildren(); }, [reloadChildren]);

  // Standing rule 15 — the sample set is buffered in memory (New / Delete /
  // cell edits are not written until "Save Samples"), so the automatic
  // edit tracker cannot see it. Register the dirty flag explicitly, or
  // navigating away would drop the whole pending set silently.
  useUnsavedGuard(dirty);

  /** The grid rows with local edits applied. */
  const rows = useMemo(
    () => samples.map(s => (edits[s.oid] ? { ...s, ...edits[s.oid] } : s)),
    [samples, edits]);

  // ── Columns — the legacy visible sets, in legacy order ────────────────────
  /** The full legacy visible set - used for Export, so nothing is lost. */
  const lookupNames = useCallback(
    (kind: string) => lookups.filter(o => o.kind === kind && o.name).map(o => o.name as string),
    [lookups]);
  const sampleExportCols: AppColumn<PreLoginSample>[] = useMemo(() => [
    // Legacy COCSettingsSamples_ListView_Copy_SampleRegistration: exactly these 17
    // columns in this order, batch (click-to-edit) cells whose edits are written
    // together by Save Samples. SampleID and Client are AllowEdit=False; SampleID
    // carries the legacy Count summary; SampleMatrix has AllowClear=False.
    { field: 'sampleID',            header: 'SampleID',        width: 100,
      summary: rs => `Count=${rs.length}` },
    { field: 'sampleName',          header: 'SampleName',      width: 107, edit: { type: 'text' } },
    { field: 'sampleTypeName',      header: 'SampleType',      width: 95,
      edit: { type: 'combo', options: lookupNames('sampleType'), clearable: true } },
    { field: 'siteName',            header: 'SiteName',        width: 120, edit: { type: 'text' } },
    { field: 'sampleMatrix',        header: 'SampleMatrix',    width: 103,
      edit: { type: 'combo', options: lookupNames('visualMatrix'), clearable: false } },
    { field: 'comment',             header: 'Comment',         width: 79,  edit: { type: 'text' } },
    { field: 'collectorPhone',      header: 'CollectorPhone',  width: 100, edit: { type: 'text' } },
    { field: 'client',              header: 'Client',          width: 212 },
    { field: 'stationLocationName', header: 'StationLocation', width: 110, edit: { type: 'text' } },
    { field: 'sampleStatusName',    header: 'SampleStatus',    width: 100,
      edit: { type: 'combo', options: lookupNames('sampleStatus'), clearable: true } },
    { field: 'remark',              header: 'Remark',          width: 100, edit: { type: 'text' } },
    { field: 'projectNumber',       header: 'ProjectNumber',   width: 110, edit: { type: 'text' } },
    { field: 'previousSample',      header: 'PreviousSample',  width: 110, edit: { type: 'text' } },
    { field: 'previousCollection',  header: 'PreviousCollection', width: 130, edit: { type: 'text' } },
    { field: 'samplingEvent',       header: 'SamplingEvent',   width: 110, edit: { type: 'text' } },
    { field: 'loginBy',             header: 'LoginBy',         width: 100 },
    { field: 'loginDateTime',       header: 'LoginDateTime',   width: 130, type: 'datetime' },
  ], [lookupNames]);

  /** The grid shows the full legacy visible set and SCROLLS horizontally (owner
   *  report 2026-09-29: a fixed-width layout took the scrollbar away and the
   *  columns could not be reached). */
  const sampleCols = sampleExportCols;

  const testCols: AppColumn<PreLoginTest>[] = useMemo(() => [
    { field: 'sampleID',      header: 'SampleID',      width: 120 },
    { field: 'matrix',        header: 'Matrix',        width: 110 },
    { field: 'test',          header: 'Test',          width: 200 },
    { field: 'testGroupName', header: 'TestGroupName', width: 150 },
    { field: 'method',        header: 'Method',        width: 130 },
    { field: 'components',    header: 'Components',    width: 140 },
    { field: 'subOut',        header: 'SubOut',        width: 80 },
    { field: 'testParams',    header: 'Parameter',     width: 110 },
  ], []);

  const bottleCols: AppColumn<PreLoginBottle>[] = useMemo(() => [
    { field: 'test',             header: 'Test',         width: 200 },
    { field: 'bottleID',         header: 'BottleID',     width: 120 },
    { field: 'method',           header: 'Method',       width: 130 },
    { field: 'containerName',    header: 'Containers',   width: 130 },
    { field: 'preservativeName', header: 'Preservative', width: 130 },
    { field: 'storageName',      header: 'StorageID',    width: 130 },
  ], []);

  /** The Parameters tab — the Tests collection UNCOLLAPSED. The Tests grid
   *  above shows DISTINCT TestMethod (legacy's Tests(7) over 13 rows); these
   *  are those rows.
   *
   *  The reader still returns MDL / RptLimit / Result / QC Type / Units / SubOut
   *  and the `hasOverride` flag that distinguishes a COC's own limit from the
   *  inherited library default. They are no longer COLUMNS (owner request
   *  2026-09-28) but the API is left intact: nothing else costs anything to
   *  keep, and stripping the SP would make restoring a column a DB change. */
  const paramCols: AppColumn<PreLoginParameter>[] = useMemo(() => [
    // Owner request 2026-09-28: the analytical columns (QC Type · Units · MDL ·
    // RptLimit · Result · SubOut) are gone and COMPONENT sits before Parameter.
    // That reads as the identity chain the tab is actually for — which sample,
    // which test, by which method, of which component, which parameter — rather
    // than the result-bearing fields, which belong to a run and are empty on a
    // template anyway (every live row on COC0433 had a blank Result).
    { field: 'sampleID',  header: 'SampleID',  width: 120 },
    { field: 'test',      header: 'Test',      width: 180 },
    { field: 'method',    header: 'Method',    width: 120 },
    { field: 'component', header: 'Component', width: 140 },
    { field: 'parameter', header: 'Parameter', width: 190 },
  ], []);


  // ── Actions ───────────────────────────────────────────────────────────────

  /** Builds the full set PUT — every row, edits applied, new rows with a null
   *  Oid. The server allocates SampleNo for the new ones race-safely. */
  const toInputs = useCallback((list: PreLoginSample[]): PreLoginSampleInput[] =>
    list.map(s => ({
      oid:                  s.oid.startsWith('new:') ? null : s.oid,
      sampleName:           s.sampleName,
      sampleTypeOid:        s.sampleTypeOid,
      siteName:             s.siteName,
      visualMatrixOid:      s.visualMatrixOid,
      comment:              s.comment,
      collectorOid:         s.collectorOid,
      collectorPhone:       s.collectorPhone,
      stationLocationName:  s.stationLocationName,
      sampleStatusOid:      s.sampleStatusOid,
      remark:               s.remark,
      projectNumber:        s.projectNumber,
      previousSample:       s.previousSample,
      previousCollection:   s.previousCollection,
      samplingEvent:        s.samplingEvent,
      qty:                  s.qty,
      containers:           s.containers,
      bottleQty:            s.bottleQty,
      preservetives:        s.preservetives,
      storageOid:           s.storageOid,
      preserveConditionOid: s.preserveConditionOid,
      qcTypeOid:            s.qcTypeOid,
      latitude:             s.latitude,
      longitude:            s.longitude,
      depth:                s.depth,
      sampleSource:         s.sampleSource,
      subOut:               s.subOut,
      hold:                 s.hold,
      sortOrder:            s.sortOrder,
    })), []);

  /** A grid cell committed - buffered; nothing is written until Save Samples. */
  const onCellCommit = useCallback((row: PreLoginSample, col: AppColumn<PreLoginSample>, value: string) => {
    const f = String(col.field);
    const idOf = (kind: string) => lookups.find(o => o.kind === kind && o.name === value)?.id ?? null;
    let patch: Partial<PreLoginSample>;
    switch (f) {
      case 'sampleTypeName':   { const id = idOf('sampleType');   patch = { sampleTypeOid: id,   sampleTypeName: id ? value : null }; break; }
      case 'sampleMatrix':     { const id = idOf('visualMatrix'); patch = { visualMatrixOid: id, sampleMatrix: id ? value : null }; break; }
      case 'sampleStatusName': { const id = idOf('sampleStatus'); patch = { sampleStatusOid: id, sampleStatusName: id ? value : null }; break; }
      default: patch = { [f]: value.trim() === '' ? null : value } as Partial<PreLoginSample>;
    }
    setEdits(e => ({ ...e, [row.oid]: { ...e[row.oid], ...patch } }));
    setDirty(true);
  }, [lookups, notify]);

  const removeSelected = useCallback(async () => {
    if (selected.size === 0) return;
    await runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Remove ${selected.size} sample${selected.size === 1 ? '' : 's'}? `
             + 'Their tests and bottle allocations are removed too. '
             + 'Nothing is written until you Save.',
      confirmLabel: 'Remove',
      successMessage: null,   // nothing has been persisted yet — no "Deleted."
      delete: () => {
        const keep = rows.filter(r => !selected.has(r.oid));
        setSamples(keep);
        setEdits(e => {
          const next = { ...e };
          for (const id of selected) delete next[id];
          return next;
        });
        setDirty(true);
        setSelected(new Set());
      },
    });
  }, [selected, rows, runDelete]);

  const save = useCallback(async () => {
    await runSave({
      formName: PRE_LOGIN_FORM,
      auditAction: 'edit',
      save: async () => {
        const body = toInputs(rows);
        await api.put(`/api/v1/pre-login-settings/${cocOid}/samples`, body);
      },
      onSaved: () => void loadSamples(),
    });
  }, [rows, cocOid, runSave, toInputs, loadSamples]);

  // ── Copy Samples / Copy Test / bottle operations ─────────────────────────
  // Each runs on the server against SAVED rows (legacy commits the grid first),
  // so a pending buffered edit blocks them rather than being silently lost.

  const apiMsg = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? fallback;

  /** The single checked, saved sample a copy runs from — or null after telling
   *  the user why not (legacy messages verbatim). */
  const copySource = useCallback((): PreLoginSample | null => {
    if (selected.size === 0) { notify.warning('Please select an item to proceed further.'); return null; }
    if (selected.size > 1) { notify.warning('Select only one check box.'); return null; }
    if (dirty) { notify.warning('Save the samples first, then copy.'); return null; }
    const row = rows.find(r => selected.has(r.oid));
    if (!row || row.oid.startsWith('new:')) { notify.warning('Save the samples first, then copy.'); return null; }
    return row;
  }, [selected, dirty, rows, notify]);

  const afterServerOp = useCallback(async () => {
    await loadSamples();
    await reloadChildren();
  }, [loadSamples, reloadChildren]);

  const openCopySamples = () => { if (copySource()) setCopy('samples'); };

  const openCopyTest = async () => {
    const src = copySource();
    if (!src) return;
    setCopy('test');
    setTargetsLoading(true);
    try {
      const res = await api.get(`/api/v1/pre-login-settings/samples/${src.oid}/copy-test-targets`);
      setTargets((res.data?.data ?? []).map((t: { oid: string; sampleID: string | null; sampleName: string | null; sampleMatrix: string | null }) => t));
    } catch { notify.error('Could not load the target samples.'); }
    finally { setTargetsLoading(false); }
  };

  const runCopySamples = (count: number) => {
    const src = copySource();
    if (!src) return;
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit',
      save: async () => { await api.post(`/api/v1/pre-login-settings/samples/${src.oid}/copy`, { count }); },
      onSaved: () => { setCopy(null); setSelected(new Set()); void afterServerOp(); },
    });
  };

  const runCopyTest = (oids: string[]) => {
    const src = copySource();
    if (!src) return;
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit',
      successMessage: oids.length === 1
        ? 'The tests copied are applied to the selected sample successfully.'
        : 'The tests copied are applied to the selected samples successfully.',
      save: async () => {
        try { await api.post(`/api/v1/pre-login-settings/samples/${src.oid}/copy-test`, { targets: oids }); }
        catch (e) { notify.error(apiMsg(e, 'Could not copy the tests.')); throw e; }
      },
      onSaved: () => { setCopy(null); void afterServerOp(); },
    });
  };

  const assignBottles = () => {
    if (!activeSample || activeSample.startsWith('new:')) { notify.warning('Select the sample id'); return; }
    if (dirty) { notify.warning('Save the samples first, then assign bottles.'); return; }
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit', successMessage: 'Bottle assigned successfully',
      save: async () => {
        try { await api.post(`/api/v1/pre-login-settings/samples/${activeSample}/assign-bottles`); }
        catch (e) { notify.error(apiMsg(e, 'Could not assign the bottles.')); throw e; }
      },
      onSaved: () => { void afterServerOp(); },
    });
  };

  const resetBottles = async () => {
    if (dirty) { notify.warning('Save the samples first, then reset bottles.'); return; }
    const ok = await confirm({
      title: 'Reset Bottles',
      message: 'This resets EVERY sample on this Pre-Login Setting to one bottle "A" per test and clears its container, preservative and storage. Continue?',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/api/v1/pre-login-settings/${cocOid}/reset-bottles`);
      notify.success('Bottles reset successfully.');
      await afterServerOp();
    } catch (e) { notify.error(apiMsg(e, 'Could not reset the bottles.')); }
  };

  const openCopyBottles = () => {
    if (samples.length < 2) { notify.warning("Add atleast two sampleID's"); return; }
    if (!activeSample || activeSample.startsWith('new:')) { notify.warning('Select the sample id'); return; }
    if (dirty) { notify.warning('Save the samples first, then copy bottles.'); return; }
    setTargets(rows.filter(r => r.oid !== activeSample).map(r => ({
      oid: r.oid, sampleID: r.sampleID, sampleName: r.sampleName, sampleMatrix: r.sampleMatrix })));
    setCopy('bottles');
  };

  const runCopyBottles = (oids: string[]) => {
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit', successMessage: 'Bottle set copied successfully.',
      save: async () => {
        try { await api.post(`/api/v1/pre-login-settings/samples/${activeSample}/copy-bottles`, { targets: oids }); }
        catch (e) { notify.error(apiMsg(e, 'Could not copy the bottles.')); throw e; }
      },
      onSaved: () => { setCopy(null); void afterServerOp(); },
    });
  };

  const activeSampleId = useMemo(
    () => rows.find(r => r.oid === activeSample)?.sampleID ?? null, [rows, activeSample]);

  return (
    <div className="bl-page sr-detail pl-detail">
      {/* ── Page header: breadcrumb back to the record, then the three
             collection tabs with their counts. Mirrors Sample Registration,
             whose Sample Login / Tests / Containers steps are likewise a
             SEPARATE page that replaces the registration form. ── */}
      <div className="bl-top">
        <div className="sr-crumb">
          <a onClick={onBack}>Settings</a>
          <span className="sr-crumb-sep">›</span>
          <a onClick={onBack}>Pre-Login Settings</a>
          <span className="sr-crumb-sep">›</span>
          <a onClick={onBack}>{cocId ?? ''}</a>
          <span className="sr-crumb-sep">›</span>
          <b>{TAB_LABEL[tab]}</b>
        </div>

        <div className="sr-savebar">
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--ai-ink)' }}>
            {cocId ? `Pre-Login Settings – ${cocId}` : 'Pre-Login Settings'}
            <span style={{ color: 'var(--ai-muted)', fontWeight: 400 }}> · {TAB_LABEL[tab]}</span>
          </h2>
          {/* View-only audit chip on the TITLE row — v4 §6/§8 ruling, never in
              an action toolbar. §8 wants it on every screen of the BO. */}
          <FormTitle pageId="set-prelogin" auditFormName={PRE_LOGIN_FORM} auditSourceId={cocOid} />
          {/* …and Project Notes beside it: §8 pairs the two components and asks
              for BOTH on every screen of the BO. This page is still that one
              COC, so the notes are the same record's — the shared button over
              the form's existing note routes (rule 8). */}
          <ProjectNotesButton
            recordId={cocOid}
            baseUrl={cocOid ? `/api/v1/pre-login-settings/${cocOid}/notes` : null}
            recordLabel={cocId ?? ''}
            businessObject={PRE_LOGIN_FORM}
          />
        </div>

        {/* The shared in-form TAB STRIP (`.sr-tabbar` / `.sr-tb`) — the SAME
            control Sample Registration's Sample Login tabs use, not the
            `.sa-filter-btn` pills this carried until 2026-09-28. These switch
            between views of one record, which is what a tab means; the orange
            pills read as filter chips narrowing a single list. */}
        <div className="sr-tabbar" style={{ marginTop: 6, display: 'flex', alignItems: 'center' }} role="tablist">
          {([
            ['samples',    `Samples (${samples.length})`],
            ['tests',      `Tests (${testCount})`],
            ['containers', `Containers (${containerCount})`],
            // Parameters sits NEXT TO Containers, matching Sample Registration's
            // own sample tabs (Sample Login · Test View · Containers · Parameters).
            // Its count is per-SAMPLE, not per-COC like Tests/Containers above —
            // the rows only exist once a sample is selected.
            ['parameters', `Parameters (${params.length})`],
          ] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id}
                    className={`sr-tb${tab === id ? ' active' : ''}`}
                    onClick={() => setTab(id)}>{label}</button>
          ))}
          {/* Back sits on the tab-strip row, right-aligned (owner request 2026-10-09). */}
          <button className="ca-btn" style={{ marginLeft: 'auto' }} onClick={onBack}
                  title={`Back to the record (${formatCombo('esc')})`}>
            <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-blue)' }} /> Back
          </button>
        </div>
      </div>

      <div className="bl-grid-area" style={{ overflow: tab === 'samples' ? 'hidden' : 'auto' }}>
        {tab === 'samples' && (
          // Sample Registration's Sample Login layout (`.sr-sl-layout`): TWO bordered
          // panels sharing the page height 50 / 50, each grid scrolling inside its own
          // panel - so a short sample list no longer leaves a half-empty card and the
          // two grids always line up. Test Assignment is the second panel.
          <div className="sr-sl-layout">
            <div className="sr-sl-panel sr-sl-samples">
              <h3 className="sr-sec-h">
                Samples{cocId ? ' - ' : ''}
                <span style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))' }}>{cocId ?? ''}</span>
                <span className="sr-sec-note">
                  click a cell to edit · Save Samples writes every change together{dirty ? ' · unsaved changes' : ''}
                </span>
              </h3>
              <AppToolbar
                showMeta={false}
                onDelete={() => void removeSelected()}
                deleteDisabled={selected.size === 0}
                onRefresh={() => void loadSamples()}
                refreshing={loading}
                left={<>
                  <button className="ca-btn ca-btn-solid" disabled={!dirty}
                          onClick={() => void save()}>
                    <i className="ti ti-device-floppy" /> Save Samples
                  </button>
                  <button className="ca-btn" onClick={openCopySamples}>
                    <i className="ti ti-copy" /> Copy Samples
                  </button>
                  <button className="ca-btn" onClick={() => void openCopyTest()}>
                    <i className="ti ti-copy" /> Copy Test
                  </button>
                </>}
                exportData={{
                  filename: 'Pre-Login Samples',
                  headers: sampleExportCols.map(c => String(c.headerText ?? c.header)),
                  rows: rows.map(r => sampleExportCols.map(c => String((r as never)[c.field] ?? ''))),
                }}
              />
              {/* Find Column - the grid is 17 columns wide (shared with Sample Login). */}
              <ColumnFinder columns={sampleExportCols.map(c => String(c.headerText ?? c.header))} gridRef={gridWrapRef} />
              <div ref={gridWrapRef} className="sr-sl-grid-wrap">
                <AppDataGrid<PreLoginSample>
                  rows={rows}
                  columns={sampleCols}
                  getRowId={r => r.oid}
                  loading={loading}
                  selectable
                  selectedIds={selected}
                  onSelectionChange={setSelected}
                  stickyColumns={2}
                  selectedRowId={activeSample}
                  onRowClick={r => setActiveSample(r.oid)}
                  onCellCommit={onCellCommit}
                  defaultPageSize={200}
                  initialSort={{ key: 'sampleID', dir: 'asc' }}
                  emptyText="No samples on this Pre-Login Setting."
                />
              </div>
            </div>

            <div className="sr-sl-panel sr-sl-tests">
              <PreLoginTestAssignment
                sampleOid={activeSample}
                sampleId={activeSampleId}
                tests={tests}
                onSaved={() => { void reloadChildren(); void loadSamples(); }}
                onRefresh={() => void reloadChildren()}
              />
            </div>
          </div>
        )}

        {/* Copy Samples / Copy Test / Copy Bottles pickers. */}
        {copy === 'samples' && (
          <CopySamplesDialog
            sampleId={rows.find(r => selected.has(r.oid))?.sampleID ?? null}
            onClose={() => setCopy(null)} onOk={runCopySamples} />
        )}
        {copy === 'test' && (
          <TargetSamplesDialog
            title="Copy Test" okLabel="Copy" loading={targetsLoading} rows={targets}
            note="Please select the sample to apply the copied tests."
            emptyText="No other sample in this Pre-Login Setting shares this sample's matrix."
            onClose={() => setCopy(null)} onOk={runCopyTest} />
        )}
        {copy === 'bottles' && (
          <TargetSamplesDialog
            title="Copy Bottles" okLabel="Copy" loading={false} rows={targets}
            note="Select at least one target sample."
            emptyText="No other samples."
            onClose={() => setCopy(null)} onOk={runCopyBottles} />
        )}


        {(tab === 'tests' || tab === 'containers' || tab === 'parameters') && (
          <section className="sr-sec">
            <div className="sr-sec-b" style={{ padding: 8 }}>
              {/* Section toolbar — v4 §4 "Class B: child grids each own a section
                  toolbar", §6 middle altitude. Refresh + Export only: tests and
                  their containers are generated from the test library, and the
                  legacy container grid is AllowNew/AllowDelete="False". */}
              <div className="pl-sectionbar">
                <span className="sr-sec-note">
                  {activeSampleId ? `Sample ${activeSampleId}` : 'select a sample on the Samples tab'}
                </span>
                <span className="pl-sectionbar-spacer" />
                {tab === 'containers' && (<>
                  <button className="ca-btn" disabled={!activeSample} onClick={assignBottles}>
                    <i className="ti ti-bottle" /> Assign Bottles
                  </button>
                  <button className="ca-btn" disabled={!activeSample} onClick={openCopyBottles}>
                    <i className="ti ti-copy" /> Copy Bottles
                  </button>
                  <button className="ca-btn" onClick={() => void resetBottles()}>
                    <i className="ti ti-restore" /> Reset Bottles
                  </button>
                </>)}
                <button className="ca-btn" disabled={!activeSample}
                        onClick={() => void reloadChildren()}>
                  <i className="ti ti-refresh" /> Refresh
                </button>
              </div>
              {tab === 'tests' && (
                <AppDataGrid<PreLoginTest>
                  rows={tests}
                  columns={testCols}
                  getRowId={r => r.oid}
                  defaultPageSize={10}
                  exportFilename="Pre-Login Tests"
                  emptyText={activeSample ? 'No tests on this sample.' : 'Select a sample on the Samples tab.'}
                />
              )}
              {tab === 'containers' && (
                <AppDataGrid<PreLoginBottle>
                  rows={bottles}
                  columns={bottleCols}
                  getRowId={r => r.oid}
                  defaultPageSize={10}
                  exportFilename="Pre-Login Containers"
                  emptyText={activeSample ? 'No containers on this sample.' : 'Select a sample on the Samples tab.'}
                />
              )}
              {tab === 'parameters' && (
                <AppDataGrid<PreLoginParameter>
                  rows={params}
                  columns={paramCols}
                  getRowId={r => r.oid}
                  defaultPageSize={20}
                  exportFilename="Pre-Login Parameters"
                  emptyText={activeSample ? 'No parameters on this sample.' : 'Select a sample on the Samples tab.'}
                />
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
